import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Loader2, ArrowLeft, Download, CheckCircle2, XCircle, AlertTriangle, Clock, Globe, Camera, ChevronDown, ChevronUp,
  GitCompare, Sparkles, Wand2, Mail, Share2,
} from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  passed: { color: "#0F9D77", icon: CheckCircle2, label: "Passed" },
  failed: { color: "#E5325F", icon: XCircle, label: "Failed" },
  blocked: { color: "#FF8A3D", icon: AlertTriangle, label: "Blocked" },
};

const evidenceUrl = (runId, file) => `${API}/agent-runs/${runId}/evidence/${file}`;

const ScriptResult = ({ r, runId }) => {
  const [open, setOpen] = useState(r.status !== "passed");
  const S = STATUS[r.status] || STATUS.blocked;
  return (
    <div className="card-soft overflow-hidden" data-testid={`result-${r.script_id}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left p-5 flex items-center gap-4 hover:bg-muted/40 transition-colors">
        <S.icon className="w-6 h-6 shrink-0" style={{ color: S.color }} />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-[#1E1560] truncate">{r.name}</div>
          <div className="text-xs text-secondary">{r.module} · {((r.duration_ms || 0) / 1000).toFixed(1)}s{r.login ? ` · login: ${r.login}` : ""}{r.mode === "scripted" ? " · scripted" : r.mode === "ai_planned" ? " · AI-planned" : ""}</div>
          {r.observed && <div className="text-xs text-[#2A1E9E] mt-0.5 truncate">Observed: {r.observed}</div>}
        </div>
        <Badge className="rounded-full" style={{ background: `${S.color}1F`, color: S.color }}>{S.label}</Badge>
        {open ? <ChevronUp className="w-4 h-4 text-secondary" /> : <ChevronDown className="w-4 h-4 text-secondary" />}
      </button>
      {open && (
        <div className="border-t border-border p-5 space-y-4">
          {r.error && <p className="text-sm text-[#E5325F] font-mono break-words" data-testid="result-error">{r.error}</p>}
          {!r.steps?.length && r.landing && (
            <div>
              <p className="text-xs text-secondary mb-2 flex items-center gap-1"><Camera className="w-3 h-3" /> Landing page captured</p>
              <img src={evidenceUrl(runId, r.landing)} alt="Landing" className="rounded-lg border border-border w-full max-w-xl" />
            </div>
          )}
          {r.steps?.map((st) => {
            const SS = STATUS[st.status] || STATUS.blocked;
            return (
              <div key={st.step} className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-4 items-start">
                <div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-mono text-xs text-secondary">0{st.step}</span>
                    <SS.icon className="w-4 h-4" style={{ color: SS.color }} />
                    <span className="text-[#1E1560]">{st.text}</span>
                  </div>
                  <p className="text-xs text-secondary mt-1 ml-7">{st.actions} browser action{st.actions === 1 ? "" : "s"}{st.error ? ` — ${st.error}` : ""}</p>
                  {st.observed && <p className="text-xs mt-1 ml-7 text-[#2A1E9E] bg-[#F1EDFB] rounded px-2 py-1 inline-block" data-testid="step-observed">Observed: {st.observed}</p>}
                </div>
                {st.screenshot && (
                  <a href={evidenceUrl(runId, st.screenshot)} target="_blank" rel="noreferrer">
                    <img src={evidenceUrl(runId, st.screenshot)} alt={`Step ${st.step}`} loading="lazy"
                      className="rounded-lg border border-border w-full hover:border-[#7C3AED]/50 transition-colors" />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DIFF = [
  ["newly_failed", "Newly broken", "#E5325F", XCircle],
  ["recovered", "Recovered", "#0F9D77", CheckCircle2],
  ["still_failing", "Still failing", "#FF8A3D", AlertTriangle],
  ["new", "New tests", "#7C3AED", Sparkles],
];

const RunComparison = ({ runId }) => {
  const [cmp, setCmp] = useState(null);
  useEffect(() => { api.get(`/agent-runs/${runId}/compare`).then((r) => setCmp(r.data)).catch(() => setCmp({ previous: null })); }, [runId]);
  if (!cmp) return null;
  if (!cmp.previous) return (
    <div className="card-soft p-5 mb-8 text-sm text-secondary flex items-center gap-2" data-testid="run-compare-empty">
      <GitCompare className="w-4 h-4 text-[#7C3AED]" /> First run for this environment — comparison will appear from the next run.
    </div>
  );
  const up = cmp.delta_pass_rate >= 0;
  return (
    <div className="card-soft p-6 mb-8" data-testid="run-compare">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-heading font-semibold text-lg text-[#1E1560] flex items-center gap-2"><GitCompare className="w-5 h-5 text-[#7C3AED]" /> Compared to previous run</h3>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-secondary font-mono text-xs">{cmp.previous.started_at?.slice(0, 16).replace("T", " ")} · {cmp.previous.pass_rate}%</span>
          <Badge className={`rounded-full ${up ? "bg-[#0F9D77]/15 text-[#0F9D77]" : "bg-[#E5325F]/15 text-[#E5325F]"}`} data-testid="run-compare-delta">
            {up ? "▲" : "▼"} {Math.abs(cmp.delta_pass_rate)} pts
          </Badge>
          <button onClick={() => window.location.assign(`/runs/${cmp.previous.id}`)} className="text-xs text-[#3A2DB8] hover:text-[#FF6B3D]" data-testid="run-compare-prev-link">Open previous</button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {DIFF.map(([key, label, color, Icon]) => (
          <div key={key} className="rounded-lg border border-border p-3" data-testid={`compare-${key}`}>
            <div className="flex items-center gap-2 mb-2"><Icon className="w-4 h-4" style={{ color }} /><span className="text-xs font-semibold" style={{ color }}>{label}</span><span className="ml-auto font-heading font-bold text-lg" style={{ color }}>{cmp[key].length}</span></div>
            <ul className="space-y-1">
              {cmp[key].slice(0, 4).map((t) => <li key={t.script_id} className="text-xs text-[#1E1560] truncate" title={t.error || t.name}>{t.name}</li>)}
              {cmp[key].length === 0 && <li className="text-xs text-secondary">—</li>}
              {cmp[key].length > 4 && <li className="text-xs text-secondary">+{cmp[key].length - 4} more</li>}
            </ul>
          </div>
        ))}
      </div>
      <p className="text-xs text-secondary mt-3">{cmp.stable.length} stable passes · {cmp.removed.length} removed</p>
    </div>
  );
};

export default function RunDetail() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [shareUrl, setShareUrl] = useState(run?.share_token ? `${window.location.origin}/share/${run.share_token}` : "");

  const share = async () => {
    try {
      const { data } = await api.post(`/agent-runs/${runId}/share`);
      const url = `${window.location.origin}${data.path}`;
      setShareUrl(url);
      try { await navigator.clipboard.writeText(url); toast.success("Share link copied to clipboard"); } catch { toast.success("Share link created"); }
    } catch { toast.error("Could not create share link"); }
  };

  useEffect(() => {
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/agent-runs/${runId}`);
        setRun(data);
        if (data.status === "running") timer = setTimeout(poll, 3000);
      } catch { toast.error("Run not found"); navigate("/dashboard"); }
    };
    poll();
    return () => clearTimeout(timer);
  }, [runId, navigate]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/agent-runs/${runId}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `trinity-report-${runId.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate report"); } finally { setDownloading(false); }
  };

  if (!run) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#3A2DB8]" /></div>;

  const running = run.status === "running";
  const pct = run.progress?.total ? Math.round((run.progress.current / run.progress.total) * 100) : 0;

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-12" data-testid="run-detail-page">
      <button onClick={() => navigate(-1)} className="text-sm text-[#3A2DB8] hover:text-[#FF6B3D] flex items-center gap-1 mb-6" data-testid="run-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline text-[#7C3AED] mb-2">{run.trigger === "scheduled" ? "Scheduled run" : "Manual run"} · {run.started_at?.slice(0, 16).replace("T", " ")} UTC</p>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#1E1560]">{run.environment_name}</h1>
          <p className="text-sm text-secondary font-mono flex items-center gap-1 mt-1"><Globe className="w-3 h-3" /> {run.env_url}</p>
        </div>
        {run.status === "completed" && (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={share} data-testid="share-report-btn" className="rounded-full border-[#3A2DB8]/30 text-[#2A1E9E] gap-2">
              <Share2 className="w-4 h-4" /> {shareUrl || run.share_token ? "Copy share link" : "Share summary"}
            </Button>
            <Button onClick={download} disabled={downloading} data-testid="download-report-btn"
              className="rounded-full bg-[#2A1E9E] hover:bg-[#3A2DB8] text-white font-semibold gap-2">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download PDF report
            </Button>
          </div>
        )}
      </div>

      {(shareUrl || run.share_token) && (
        <p className="text-xs text-secondary mb-6 font-mono break-all" data-testid="share-url">Public link: {shareUrl || `${window.location.origin}/share/${run.share_token}`}</p>
      )}
      {running && (
        <div className="card-soft p-6 mb-8" data-testid="run-progress">
          <div className="flex items-center gap-3 mb-3">
            <Loader2 className="w-5 h-5 animate-spin text-[#7C3AED]" />
            <span className="font-heading font-semibold text-[#1E1560]">Agents testing in a live browser…</span>
            <span className="ml-auto font-mono text-sm text-secondary">{run.progress?.current || 0}/{run.progress?.total || 0}</span>
          </div>
          <Progress value={pct} className="h-2 bg-muted" />
          <p className="text-sm text-secondary mt-3 truncate">Now: {run.progress?.script}</p>
        </div>
      )}

      {run.status === "failed" && (
        <div className="card-soft p-6 mb-8 border-[#E5325F]/40" data-testid="run-error">
          <div className="flex items-center gap-2 text-[#E5325F] font-semibold"><XCircle className="w-5 h-5" /> Run failed</div>
          <p className="text-sm font-mono mt-2 text-secondary">{run.error}</p>
        </div>
      )}

      {run.status === "completed" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            {[["Verdict", run.release_ready ? "Release ready" : "Needs attention", run.release_ready ? "#0F9D77" : "#FF8A3D"],
              ["Pass rate", `${run.pass_rate}%`, "#3A2DB8"], ["Passed", run.passed, "#0F9D77"],
              ["Failed", run.failed, "#E5325F"], ["Blocked", run.blocked, "#FF8A3D"]].map(([l, v, c]) => (
              <div key={l} className="card-soft p-5" data-testid={`run-stat-${l.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="text-xs text-secondary mb-1">{l}</div>
                <div className="font-heading font-bold text-2xl" style={{ color: c }}>{v}</div>
              </div>
            ))}
          </div>
          <RunComparison runId={run.id} />
          {(run.self_healed?.length > 0 || run.email_status) && (
            <div className="flex flex-wrap gap-3 mb-8 text-xs">
              {run.self_healed?.length > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#7C3AED]/10 text-[#7C3AED] px-3 py-1.5" data-testid="run-self-healed">
                  <Wand2 className="w-3.5 h-3.5" /> {run.self_healed.length} impacted script{run.self_healed.length === 1 ? "" : "s"} self-healed — awaiting re-approval
                </span>
              )}
              {run.email_status && (
                <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 ${run.email_status.sent ? "bg-[#0F9D77]/10 text-[#0F9D77]" : "bg-muted text-secondary"}`} data-testid="run-email-status" title={run.email_status.reason}>
                  <Mail className="w-3.5 h-3.5" /> {run.email_status.sent ? `Report emailed to ${run.email_status.to}` : "Report not emailed"}
                </span>
              )}
            </div>
          )}
        </>
      )}

      <div className="space-y-3">
        {run.results?.map((r) => <ScriptResult key={r.script_id} r={r} runId={run.id} />)}
        {!run.results?.length && !running && <p className="text-secondary text-sm flex items-center gap-2"><Clock className="w-4 h-4" /> No results recorded.</p>}
      </div>
    </div>
  );
}
